
import React from 'react';
import { Button } from '../../../components/base/Button';

export const ServicesSection: React.FC = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">AI Photo Enhancement Services</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transform your existing photos into high-performing assets using advanced AI enhancement. 
            Get better versions of what you already have – no new photoshoot needed!
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-6">
              <i className="ri-upload-line text-white text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">1. Upload Your Photos</h3>
            <p className="text-gray-600">
              Simply upload your current listing photos through our secure platform. 
              We accept all common formats and handle multiple images at once.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-full mx-auto mb-6">
              <i className="ri-magic-line text-white text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">2. Professional Enhancement</h3>
            <p className="text-gray-600">
              Our professional editors enhance lighting and color correction, expertly declutter and digitally stage empty rooms with high-ROI furniture and decor, apply 'Golden Hour' lighting effects, and transform busy scenes into clean, appealing spaces.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-purple-600 rounded-full mx-auto mb-6">
              <i className="ri-download-line text-white text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">3. Boost Bookings</h3>
            <p className="text-gray-600">
              Download your enhanced photos and watch your bookings increase. 
              Most clients see results within the first week of updating their listings.
            </p>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-2xl p-8 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Core AI Enhancement Package</h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 text-xl mr-3"></i>
                  <span className="text-gray-700">AI-driven sharpening and clarity enhancement</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 text-xl mr-3"></i>
                  <span className="text-gray-700">Professional color correction and saturation</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 text-xl mr-3"></i>
                  <span className="text-gray-700">Exposure balancing and lighting optimization</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 text-xl mr-3"></i>
                  <span className="text-gray-700">Noise reduction and image cleanup</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 text-xl mr-3"></i>
                  <span className="text-gray-700">Platform-specific optimization (Airbnb, VRBO, etc.)</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 text-xl mr-3"></i>
                  <span className="text-gray-700">
                    <a href="/blog" className="text-blue-600 hover:text-blue-700 underline">
                      24-48 hour turnaround time
                    </a>
                  </span>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6 border-2 border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-2xl font-bold text-gray-900">
                    <a href="/pricing-guide" className="text-blue-600 hover:text-blue-700 underline">
                      Starting at $250
                    </a>
                  </h4>
                  <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                    Limited Time
                  </span>
                </div>
                <p className="text-gray-600 mb-4">Up to 10 photos enhanced</p>
                <Button onClick={scrollToContact} className="w-full">
                  Get Started Now
                </Button>
              </div>
            </div>
            
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/94a94ba3ada14351cd22753f697f5af7.jpg"
                alt="AI Enhancement Demo"
                className="rounded-lg shadow-xl w-full h-96 object-cover object-top"
              />
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Optional Add-On Services</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-line text-blue-600 text-xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                <a href="/virtual-staging" className="text-blue-600 hover:text-blue-700 underline">
                  Virtual Staging
                </a>
              </h4>
              <p className="text-gray-600 text-sm mb-4">Add furniture and decor to empty rooms</p>
              <p className="text-blue-600 font-bold">+$50 per room</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mx-auto mb-4">
                <i className="ri-eraser-line text-green-600 text-xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Object Removal</h4>
              <p className="text-gray-600 text-sm mb-4">Remove unwanted items or clutter</p>
              <p className="text-green-600 font-bold">+$25 per photo</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg mx-auto mb-4">
                <i className="ri-landscape-line text-purple-600 text-xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Sky Replacement</h4>
              <p className="text-gray-600 text-sm mb-4">Perfect blue skies for exterior shots</p>
              <p className="text-purple-600 font-bold">+$30 per photo</p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-lg font-semibold text-gray-900 mb-2">
              <span className="text-red-600">Remember:</span> 
              <button 
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-blue-600 hover:text-blue-700 underline cursor-pointer ml-1"
              >
                No New Photoshoot Required
              </button>!
            </p>
            <p className="text-gray-600">
              We transform your existing images into professional-quality photos that drive bookings.
            </p>
          </div>
        </div>
      </div>

      {/* Service Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": [
            {
              "@type": "Service",
              "position": 1,
              "name": "HDR Processing",
              "description": "Professional HDR processing to balance lighting and create stunning, natural-looking photos",
              "provider": {
                "@type": "Organization",
                "name": "VistaBoost Imaging"
              }
            },
            {
              "@type": "Service", 
              "position": 2,
              "name": "Color Correction",
              "description": "Expert color correction to ensure accurate, vibrant colors that showcase your property beautifully",
              "provider": {
                "@type": "Organization",
                "name": "VistaBoost Imaging"
              }
            },
            {
              "@type": "Service",
              "position": 3,
              "name": "Brightness & Contrast",
              "description": "Optimal brightness and contrast adjustments to make your photos bright, welcoming, and professional",
              "provider": {
                "@type": "Organization",
                "name": "VistaBoost Imaging"
              }
            },
            {
              "@type": "Service",
              "position": 4,
              "name": "Perspective Correction",
              "description": "Professional perspective correction to eliminate distortion and create perfectly straight lines",
              "provider": {
                "@type": "Organization",
                "name": "VistaBoost Imaging"
              }
            },
            {
              "@type": "Service",
              "position": 5,
              "name": "Object Removal",
              "description": "Clean removal of unwanted objects, clutter, and distractions for pristine property photos",
              "provider": {
                "@type": "Organization",
                "name": "VistaBoost Imaging"
              }
            },
            {
              "@type": "Service",
              "position": 6,
              "name": "Virtual Staging",
              "description": "Add furniture and decor elements to empty spaces to help guests visualize the potential",
              "provider": {
                "@type": "Organization",
                "name": "VistaBoost Imaging"
              }
            }
          ]
        })}
      </script>
    </section>
  );
};

export default ServicesSection;
